/**
 * Template Marketplace — validation schemas & utility types.
 */

import { z } from "zod";

// ── Template categories ─────────────────────────────────────────────
export const TEMPLATE_CATEGORIES = [
  { value: "customer-support", label: "Customer Support", icon: "💬", color: "from-blue-500 to-cyan-600" },
  { value: "sales", label: "Sales & Marketing", icon: "📈", color: "from-emerald-500 to-teal-600" },
  { value: "education", label: "Education", icon: "🎓", color: "from-purple-500 to-indigo-600" },
  { value: "ecommerce", label: "E-Commerce", icon: "🛒", color: "from-orange-500 to-amber-600" },
  { value: "healthcare", label: "Healthcare", icon: "🏥", color: "from-red-500 to-pink-600" },
  { value: "real-estate", label: "Real Estate", icon: "🏠", color: "from-teal-500 to-green-600" },
  { value: "legal", label: "Legal", icon: "⚖️", color: "from-slate-500 to-zinc-600" },
  { value: "hr", label: "HR & Recruiting", icon: "👥", color: "from-violet-500 to-purple-600" },
  { value: "finance", label: "Finance", icon: "💰", color: "from-yellow-500 to-orange-600" },
  { value: "saas", label: "SaaS", icon: "☁️", color: "from-sky-500 to-blue-600" },
  { value: "internal", label: "Internal Tools", icon: "🔧", color: "from-gray-500 to-slate-600" },
  { value: "general", label: "General", icon: "🤖", color: "from-indigo-500 to-violet-600" },
] as const;

export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number]["value"];

// ── Zod schemas ─────────────────────────────────────────────────────

function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export const publishTemplateSchema = z.object({
  chatbotId: z.string().min(1),
  name: z.string().min(2).max(100),
  shortDescription: z.string().max(200).default(""),
  description: z.string().max(5000).default(""),
  category: z.string().default("general"),
  tags: z.array(z.string().max(30)).max(10).default([]),
  thumbnailUrl: z.string().url().optional().or(z.literal("")),
});

export const updateTemplateSchema = z.object({
  name: z.string().min(2).max(100).optional(),
  shortDescription: z.string().max(200).optional(),
  description: z.string().max(5000).optional(),
  category: z.string().optional(),
  tags: z.array(z.string().max(30)).max(10).optional(),
  thumbnailUrl: z.string().url().optional().or(z.literal("")),
  isPublic: z.boolean().optional(),
  status: z.enum(["draft", "published", "archived"]).optional(),
});

export const browseTemplatesSchema = z.object({
  category: z.string().optional(),
  search: z.string().max(100).optional(),
  sort: z.enum(["popular", "newest", "featured"]).default("popular"),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(12),
});

// ── Chatbot fields to copy into a template ──────────────────────────
export const TEMPLATE_CONFIG_FIELDS = [
  "prompt",
  "welcomeMessage",
  "chatbotErrorMessage",
  "provider",
  "chatTitle",
  "chatMessagePlaceHolder",
  "bubbleColor",
  "bubbleTextColor",
  "chatHeaderBackgroundColor",
  "chatHeaderTextColor",
  "chatbotReplyBackgroundColor",
  "chatbotReplyTextColor",
  "userReplyBackgroundColor",
  "userReplyTextColor",
  "chatBackgroundColor",
  "sendButtonColor",
  "borderRadius",
  "chatInputStyle",
  "chatbotLogoURL",
  "inquiryEnabled",
  "inquiryLinkText",
  "inquiryTitle",
  "inquirySubtitle",
  "inquiryEmailLabel",
  "inquiryMessageLabel",
  "inquirySendButtonText",
  "inquiryAutomaticReplyText",
  "inquiryDisplayLinkAfterXMessage",
  "displayBranding",
  "chatFileAttachementEnabled",
  "maxCompletionTokens",
  "maxPromptTokens",
  "enabledTools",
] as const;

// ── Generate unique slug ────────────────────────────────────────────
export function generateSlug(name: string, suffix?: string): string {
  const base = slugify(name);
  return suffix ? `${base}-${suffix}` : base;
}

// ── Generate referral code ──────────────────────────────────────────
export function generateReferralCode(): string {
  return Math.random().toString(36).slice(2, 10);
}

// ── Serialized template type for the frontend ───────────────────────
export interface TemplateCardData {
  id: string;
  slug: string;
  name: string;
  shortDescription: string;
  category: string;
  tags: string[];
  thumbnailUrl: string | null;
  provider: string;
  modelName: string;
  installCount: number;
  viewCount: number;
  isFeatured: boolean;
  createdAt: string;
  author: {
    id: string;
    name: string | null;
    image: string | null;
  };
}

export interface TemplateDetailData extends TemplateCardData {
  description: string;
  prompt: string | null;
  welcomeMessage: string;
  chatTitle: string;
  bubbleColor: string;
  bubbleTextColor: string;
  chatHeaderBackgroundColor: string;
  chatHeaderTextColor: string;
  chatbotReplyBackgroundColor: string;
  chatbotReplyTextColor: string;
  userReplyBackgroundColor: string;
  userReplyTextColor: string;
  chatBackgroundColor: string;
  sendButtonColor: string;
  borderRadius: string;
  chatInputStyle: string;
  enabledTools: unknown;
  shareCount: number;
  status: string;
  isPublic: boolean;
}
